// "Portal branding" section of the Clients and Resellers add/edit forms: logo,
// colours, theme (light/dark), layout and the small set of contact details the
// portal shows. Text fields go out with the normal create/update; images are
// uploaded right after (uploadPendingBranding) because a new client / reseller
// has no id until it's created. A client that belongs to a reseller shows the
// reseller's look (inheritedFrom), so only its own login name, timezone and
// welcome text are editable. Backend: helpers/clientBranding.js, v4.9 + v5.0.
import { useMemo, useEffect } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { clientAdminApi, resellerAdminApi } from '../services/api'

export const BRANDING_FIELDS = [
    'portal_slug', 'portal_title', 'brand_primary_color', 'brand_accent_color', 'portal_theme', 'portal_layout',
    'support_email', 'support_phone', 'website', 'timezone', 'portal_welcome_text'
]
export const brandingDefaults = (c = {}) => Object.fromEntries(
    BRANDING_FIELDS.map(f => [f, c[f] ?? (f === 'portal_theme' ? 'auto' : f === 'portal_layout' ? 'classic' : '')])
)

// Whole-portal layouts (same keys as LAYOUTS in helpers/clientBranding.js and
// the portals' components/layouts/). `sketch` draws the thumbnail.
export const LAYOUTS = [
    { key: 'classic', label: 'Classic', hint: 'Collapsible sidebar on the left' },
    { key: 'topbar', label: 'Top bar', hint: 'Navigation across the top, full-width pages' },
    { key: 'rail', label: 'Icon rail', hint: 'Slim icon rail + header bar' },
    { key: 'panel', label: 'Brand panel', hint: 'Sidebar filled with the primary colour' },
    { key: 'minimal', label: 'Minimal', hint: 'Centred page with tab navigation' }
]
export const pickBranding = (form) => Object.fromEntries(BRANDING_FIELDS.map(f => [f, form[f] ?? '']))

const IMAGES = [
    { kind: 'logo', column: 'brand_logo', label: 'Logo', hint: 'For light backgrounds' },
    { kind: 'logo_dark', column: 'brand_logo_dark', label: 'Logo (dark mode)', hint: 'Optional — falls back to the logo' },
    { kind: 'favicon', column: 'brand_favicon', label: 'Favicon', hint: 'Optional — square, 64 px+' }
]
const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon'
const MAX_BYTES = 2 * 1024 * 1024
const fileUrl = (name) => `/api/client/branding/file/${name}`
const slugify = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
const TIMEZONES = (() => { try { return Intl.supportedValuesOf('timeZone') } catch (_) { return [] } })()

// After the client / reseller is saved: upload chosen images / remove cleared
// ones. pending = { logo: File | 'remove', ... }. Returns the first error, if any.
export async function uploadPendingBranding(id, pending, owner = 'client') {
    const api = owner === 'reseller' ? resellerAdminApi : clientAdminApi
    for (const [kind, v] of Object.entries(pending || {})) {
        try {
            if (v === 'remove') await api.deleteBrandingImage(id, kind)
            else if (v) await api.uploadBrandingImage(id, kind, v)
        } catch (e) {
            return `${kind.replace('_', ' ')}: ${e.response?.data?.error || e.message}`
        }
    }
    return null
}

function ImagePicker({ img, current, pending, setPending, onError }) {
    const preview = useMemo(() => (pending[img.kind] instanceof File ? URL.createObjectURL(pending[img.kind]) : null), [pending, img.kind])
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])
    const shown = preview || (pending[img.kind] === 'remove' ? null : current ? fileUrl(current) : null)

    function choose(e) {
        const f = e.target.files?.[0]
        e.target.value = ''
        if (!f) return
        if (f.size > MAX_BYTES) return onError(`${img.label} must be 2 MB or smaller`)
        onError(null)
        setPending(p => ({ ...p, [img.kind]: f }))
    }
    return (
        <div className="tw-flex tw-flex-col tw-gap-1.5">
            <span className="tw-text-xs tw-font-semibold tw-text-muted">{img.label}</span>
            <div className="tw-flex tw-h-20 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-dashed tw-border-border tw-bg-input"
                style={img.kind === 'logo_dark' ? { background: '#111827' } : undefined}>
                {shown ? <img src={shown} alt="" className="tw-max-h-16 tw-max-w-[90%] tw-object-contain" />
                    : <ImageIcon size={22} className="tw-text-muted tw-opacity-50" />}
            </div>
            <div className="tw-flex tw-gap-1.5">
                <label className="btn btn-secondary btn-sm tw-flex-1 tw-cursor-pointer tw-justify-center">
                    <Upload size={12} /> {shown ? 'Replace' : 'Upload'}
                    <input type="file" accept={ACCEPT} onChange={choose} className="tw-hidden" />
                </label>
                {shown && (
                    <button type="button" className="btn btn-icon btn-sm" title="Remove"
                        onClick={() => setPending(p => ({ ...p, [img.kind]: current ? 'remove' : undefined }))}>
                        <X size={12} />
                    </button>
                )}
            </div>
            <span className="tw-text-[11px] tw-text-muted">{img.hint}</span>
        </div>
    )
}

// Thumbnail of a layout, in the given colours.
function LayoutSketch({ layout, primary, accent, dark }) {
    const bg = dark ? '#0f172a' : '#f1f5f9', card = dark ? '#1e293b' : '#ffffff', line = dark ? '#334155' : '#cbd5e1'
    const cards = (x, y, w) => (
        <>
            <rect x={x} y={y} width={w / 2 - 2} height="10" rx="1.5" fill={card} />
            <rect x={x + w / 2 + 2} y={y} width={w / 2 - 2} height="10" rx="1.5" fill={card} />
            <rect x={x} y={y + 14} width={w} height="14" rx="1.5" fill={card} />
            <rect x={x} y={y + 14} width={w * 0.35} height="3" rx="1" fill={accent} />
        </>
    )
    return (
        <svg viewBox="0 0 80 52" className="tw-block tw-h-auto tw-w-full" aria-hidden="true">
            <rect width="80" height="52" fill={bg} />
            {layout === 'classic' && (<>
                <rect width="20" height="52" fill={card} /><rect x="4" y="4" width="12" height="4" rx="1" fill={primary} />
                {[14, 20, 26, 32].map(y => <rect key={y} x="4" y={y} width="12" height="2.5" rx="1" fill={line} />)}
                {cards(25, 6, 50)}
            </>)}
            {layout === 'topbar' && (<>
                <rect width="80" height="9" fill={card} /><rect x="4" y="2.5" width="10" height="4" rx="1" fill={primary} />
                {[20, 30, 40, 50].map(x => <rect key={x} x={x} y="3.5" width="7" height="2" rx="1" fill={line} />)}
                {cards(6, 14, 68)}
            </>)}
            {layout === 'rail' && (<>
                <rect width="9" height="52" fill={card} /><rect x="2" y="2" width="5" height="5" rx="1" fill={primary} />
                {[12, 18, 24, 30].map(y => <rect key={y} x="3" y={y} width="3" height="3" rx="1" fill={line} />)}
                <rect x="9" width="71" height="8" fill={card} opacity="0.6" />
                {cards(14, 13, 61)}
            </>)}
            {layout === 'panel' && (<>
                <rect width="24" height="52" fill={primary} /><rect x="4" y="4" width="14" height="4" rx="1" fill="#ffffff" opacity="0.9" />
                {[14, 20, 26, 32].map(y => <rect key={y} x="4" y={y} width="14" height="2.5" rx="1" fill="#ffffff" opacity="0.45" />)}
                {cards(29, 6, 46)}
            </>)}
            {layout === 'minimal' && (<>
                <rect x="30" y="3" width="20" height="4" rx="1" fill={primary} />
                {[18, 30, 42, 54].map(x => <rect key={x} x={x} y="10" width="9" height="2.5" rx="1.2" fill={x === 18 ? accent : line} />)}
                {cards(14, 17, 52)}
            </>)}
        </svg>
    )
}

function LayoutPicker({ value, onChange, primary, accent, dark }) {
    return (
        <div className="form-group">
            <label>Portal layout</label>
            <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-5">
                {LAYOUTS.map(l => {
                    const on = (value || 'classic') === l.key
                    return (
                        <button type="button" key={l.key} onClick={() => onChange(l.key)} title={l.hint}
                            className="tw-flex tw-cursor-pointer tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-bg-transparent tw-p-1.5 tw-text-left"
                            style={{ borderColor: on ? primary : 'var(--border)', boxShadow: on ? `0 0 0 1px ${primary}` : 'none' }}>
                            <div className="tw-overflow-hidden tw-rounded"><LayoutSketch layout={l.key} primary={primary} accent={accent} dark={dark} /></div>
                            <span className="tw-text-xs tw-font-semibold" style={{ color: on ? primary : 'var(--text-primary)' }}>{l.label}</span>
                        </button>
                    )
                })}
            </div>
            <span className="tw-text-[11px] tw-text-muted">{LAYOUTS.find(l => l.key === (value || 'classic'))?.hint}. Changes the portal's whole layout.</span>
        </div>
    )
}

const colorOr = (v, d) => (/^#[0-9a-f]{6}$/i.test(v || '') ? v : d)

export default function ClientBrandingFields({ formData, setFormData, client, pending, setPending, onError, owner = 'client', inheritedFrom = null }) {
    const set = (k, v) => setFormData(f => ({ ...f, [k]: v }))
    const isReseller = owner === 'reseller'
    const primary = colorOr(formData.brand_primary_color, '#dc2626')
    const accent = colorOr(formData.brand_accent_color, '#f59e0b')
    const dark = formData.portal_theme === 'dark'
    const logoFile = pending.logo instanceof File ? pending.logo : null
    const logoSrc = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : (pending.logo !== 'remove' && client?.brand_logo ? fileUrl(client.brand_logo) : null)), [logoFile, pending.logo, client])
    const title = formData.portal_title || formData.name || (isReseller ? 'Reseller Portal' : 'Client Portal')
    const portalHost = isReseller ? 'reseller-portal.byredstone.com' : 'portal.byredstone.com'
    const slugParam = isReseller ? 'r' : 'c'

    const slugField = (
        <div className="form-group">
            <label>Portal URL name</label>
            <div className="tw-flex tw-gap-1.5">
                <input value={formData.portal_slug} onChange={e => set('portal_slug', e.target.value.toLowerCase())} maxLength={60} placeholder="e.g. ace-solution" />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => set('portal_slug', slugify(formData.portal_title || formData.name))} title="Make one from the name">Auto</button>
            </div>
            {formData.portal_slug && <span className="tw-text-[11px] tw-text-muted">Branded login: {portalHost}/?{slugParam}={formData.portal_slug}</span>}
        </div>
    )
    const timezoneField = (
        <div className="form-group">
            <label>Timezone</label>
            <input list="client-tz-list" value={formData.timezone} onChange={e => set('timezone', e.target.value)} placeholder="e.g. America/New_York" />
            <datalist id="client-tz-list">{TIMEZONES.map(z => <option key={z} value={z} />)}</datalist>
        </div>
    )
    const welcomeField = (
        <div className="form-group">
            <label>Welcome message</label>
            <input value={formData.portal_welcome_text} onChange={e => set('portal_welcome_text', e.target.value)} maxLength={500} placeholder="Shown on the portal dashboard" />
        </div>
    )

    // A reseller's client: the portal wears the reseller's branding.
    if (inheritedFrom) {
        const r = inheritedFrom
        const rPrimary = colorOr(r.brand_primary_color, '#dc2626')
        const rAccent = colorOr(r.brand_accent_color, '#f59e0b')
        const rLayout = LAYOUTS.find(l => l.key === (r.portal_layout || 'classic'))
        return (
            <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-p-4">
                <div>
                    <div className="tw-text-sm tw-font-semibold">Client portal branding</div>
                    <div className="tw-text-xs tw-text-muted">
                        This client belongs to <strong>{r.name}</strong>, so its portal uses {r.name}'s logo, colours, theme and layout.
                        Change them on the Resellers page.
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-border tw-p-2">
                    <div className="tw-w-28 tw-shrink-0 tw-overflow-hidden tw-rounded">
                        <LayoutSketch layout={rLayout?.key || 'classic'} primary={rPrimary} accent={rAccent} dark={r.portal_theme === 'dark'} />
                    </div>
                    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-text-xs">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {r.brand_logo && <img src={fileUrl(r.brand_logo)} alt="" className="tw-h-5 tw-max-w-[100px] tw-object-contain" />}
                            <span className="tw-truncate tw-font-semibold">{r.portal_title || r.company || r.name}</span>
                        </div>
                        <span className="tw-text-muted">Layout: {rLayout?.label} · Theme: {r.portal_theme || 'auto'}</span>
                        <span className="tw-flex tw-items-center tw-gap-1.5">
                            <span className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-sm" style={{ background: rPrimary }} />
                            <span className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-sm" style={{ background: rAccent }} />
                        </span>
                    </div>
                </div>
                <div className="form-row">{slugField}{timezoneField}</div>
                {welcomeField}
            </div>
        )
    }

    return (
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-p-4">
            <div>
                <div className="tw-text-sm tw-font-semibold">{isReseller ? 'Reseller portal branding' : 'Client portal branding'}</div>
                <div className="tw-text-xs tw-text-muted">
                    {isReseller
                        ? "How this reseller's portal looks — and the client portal of every client assigned to this reseller. All optional."
                        : "How this client's portal looks to their users. All optional."}
                </div>
            </div>

            {/* live preview */}
            <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-border" style={{ background: dark ? '#0f172a' : '#f8fafc' }}>
                <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2" style={{ background: primary }}>
                    {logoSrc ? <img src={logoSrc} alt="" className="tw-h-6 tw-max-w-[120px] tw-object-contain" />
                        : <div className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-bg-white/25 tw-text-xs tw-font-bold tw-text-white">{title[0]}</div>}
                    <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-white">{title}</span>
                </div>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2.5">
                    <span className="tw-truncate tw-text-xs" style={{ color: dark ? '#cbd5e1' : '#334155' }}>{formData.portal_welcome_text || 'Welcome back!'}</span>
                    <span className="tw-shrink-0 tw-rounded tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-white" style={{ background: accent }}>Button</span>
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Portal title</label>
                    <input value={formData.portal_title} onChange={e => set('portal_title', e.target.value)} maxLength={100} placeholder={formData.name || 'Shown in the portal header'} />
                </div>
                {slugField}
            </div>

            <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
                {IMAGES.map(img => (
                    <ImagePicker key={img.kind} img={img} current={client?.[img.column]} pending={pending} setPending={setPending} onError={onError} />
                ))}
            </div>

            <div className="form-row">
                {[['brand_primary_color', 'Primary colour', 'Header, buttons, links'], ['brand_accent_color', 'Accent colour', 'Highlights, charts']].map(([k, label, hint]) => (
                    <div className="form-group" key={k}>
                        <label>{label}</label>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <input type="color" value={colorOr(formData[k], k === 'brand_primary_color' ? '#dc2626' : '#f59e0b')}
                                onChange={e => set(k, e.target.value)} className="tw-h-9 tw-w-12 tw-cursor-pointer tw-p-0.5" />
                            <input value={formData[k]} onChange={e => set(k, e.target.value)} maxLength={7} placeholder="#1a73e8" className="tw-font-mono" />
                            {formData[k] && <button type="button" className="btn btn-icon btn-sm" onClick={() => set(k, '')} title="Use the default"><X size={12} /></button>}
                        </div>
                        <span className="tw-text-[11px] tw-text-muted">{hint}</span>
                    </div>
                ))}
            </div>

            <LayoutPicker value={formData.portal_layout} onChange={v => set('portal_layout', v)} primary={primary} accent={accent} dark={dark} />

            <div className="form-row">
                <div className="form-group">
                    <label>Default theme</label>
                    <select value={formData.portal_theme} onChange={e => set('portal_theme', e.target.value)}>
                        <option value="auto">Auto (light by day, dark at night)</option>
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                    </select>
                </div>
                {timezoneField}
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Support email</label>
                    <input type="email" value={formData.support_email} onChange={e => set('support_email', e.target.value)} placeholder="Shown in the portal's help" />
                </div>
                <div className="form-group">
                    <label>Support phone</label>
                    <input value={formData.support_phone} onChange={e => set('support_phone', e.target.value)} maxLength={50} />
                </div>
            </div>
            <div className="form-row">
                <div className="form-group">
                    <label>Website</label>
                    <input value={formData.website} onChange={e => set('website', e.target.value)} placeholder="https://…" />
                </div>
                {welcomeField}
            </div>
        </div>
    )
}
